/** Working sample rate for every decoded buffer, so DSP never has to resample mid-graph. */
export const WORKING_SAMPLE_RATE = 48_000;

export class AudioDecodeError extends Error {
  constructor(fileName: string, reason: string) {
    super(`音声をデコードできませんでした (${fileName}): ${reason}`);
    this.name = "AudioDecodeError";
  }
}

/**
 * Decodes to the working sample rate. `decodeAudioData` resamples to the context rate,
 * so creating the context at 48 kHz is what performs the conversion.
 */
export async function decodeAudioFile(file: File): Promise<AudioBuffer> {
  const context = new OfflineAudioContext(1, 1, WORKING_SAMPLE_RATE);
  try {
    return await context.decodeAudioData(await file.arrayBuffer());
  } catch (error) {
    throw new AudioDecodeError(file.name, error instanceof Error ? error.message : "不明なエラー");
  }
}

/** Interleaved Float32, the layout used at the WASM boundary. */
export function interleave(
  channels: readonly Float32Array[],
  frameCount: number,
): Float32Array<ArrayBuffer> {
  const channelCount = channels.length;
  const output = new Float32Array(frameCount * channelCount);
  for (let channel = 0; channel < channelCount; channel += 1) {
    const source = channels[channel];
    if (!source) continue;
    for (let frame = 0; frame < frameCount; frame += 1) {
      output[frame * channelCount + channel] = source[frame] ?? 0;
    }
  }
  return output;
}

/**
 * Stored PCM is 16-bit.
 *
 * Float32 doubles the bytes for a difference nobody can hear on material that arrived as
 * a lossy file, and the size is what decides whether a long recording opens at all: an
 * hour of stereo is 345 MB here against 691 MB as Float32.
 */
export function toInt16(samples: Float32Array): Int16Array<ArrayBuffer> {
  const output = new Int16Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) {
    const value = samples[index] ?? 0;
    const clamped = value < -1 ? -1 : value > 1 ? 1 : value;
    output[index] = Math.round(clamped * 32_767);
  }
  return output;
}

export function fromInt16(samples: Int16Array): Float32Array<ArrayBuffer> {
  const output = new Float32Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) {
    output[index] = (samples[index] ?? 0) / 32_767;
  }
  return output;
}

/** Channel data lifted out of an AudioBuffer so it can be transferred to a worker. */
export function detachChannels(buffer: AudioBuffer): {
  channels: Float32Array<ArrayBuffer>[];
  frameCount: number;
} {
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, index) => {
    const copy = new Float32Array(buffer.length);
    buffer.copyFromChannel(copy, index);
    return copy;
  });
  return { channels, frameCount: buffer.length };
}

export function deinterleave(samples: Float32Array, channelCount: number): Float32Array[] {
  const frames = Math.floor(samples.length / channelCount);
  return Array.from({ length: channelCount }, (_, channel) => {
    const output = new Float32Array(frames);
    for (let frame = 0; frame < frames; frame += 1) {
      output[frame] = samples[frame * channelCount + channel] ?? 0;
    }
    return output;
  });
}
